import { createI18n } from 'vue-i18n'

const messages = {
  en: {
    common: {
      back: "Back"
    },
    businessDetails: {
      title: "Let's start with your business details",
      fast: "fast",
      with: "with",
      subtitle: "The first step to pursuing your passion starts here.",
      businessName: {
        label: "What would you like to name your LLC?",
        placeholder: "Business name (e.g., Tartoro LLC)",
        errors: {
          required: "Business name is required",
          minLength: "Business name must be at least 2 characters",
          maxLength: "Business name cannot exceed 100 characters",
          format: "Business name can only contain letters, numbers, spaces, and basic punctuation"
        }
      },
      alternativeName: {
        label: "Alternative LLC Name (if first choice is unavailable)",
        placeholder: "Optional: Only if your preferred name is taken"
      },
      formationState: {
        label: "Choose State of Formation",
        placeholder: "Select your LLC's state of formation",
        errors: {
          required: "Please select a state of formation"
        }
      },
      next: "Next"
    },
    businessExperience: {
      title: "How much experience do you have running a business",
      options: {
        none: "None",
        some: "Some",
        lots: "Lots"
      },
      next: "Next",
      skip: "Skip for now"
    },
    businessEmployees: {
      title: "Will your business have employees in the first year ?",
      options: {
        none: "No, Just me",
        few: "Yes(1-5)",
        many: "Yes (6+)"
      },
      next: "Next",
      skip: "Skip for now"
    },
    businessAddress: {
      title: "Will your business address be different from your home address?",
      options: {
        no: "No",
        yes: "Yes"
      },
      form: {
        street: {
          label: "Street Address",
          placeholder: "Enter street address"
        },
        city: {
          label: "City",
          placeholder: "Enter city"
        },
        state: {
          label: "State",
          placeholder: "Select state"
        },
        zipCode: {
          label: "ZIP Code",
          placeholder: "Enter ZIP code"
        }
      },
      next: "Next",
      skip: "Skip for now"
    },
    businessProgress: {
      title: "How far have you come with building your business?",
      options: {
        launching: "Launching Soon (1-2 months)",
        making_money: "Already making money",
        planning: "planning for the future"
      },
      next: "Next",
      skip: "Skip for now"
    },
    businessGoals: {
      title: "What is your business aiming to achieve in the near or long term??",
      options: {
        side: "Side business",
        primary: "Primary income",
        next_big: "The next big thing"
      },
      next: "Next",
      skip: "Skip for now"
    },
    businessCategory: {
      title: "What kind of business are you forming?",
      subtitle: "Now, pick a category for your business. For example: landscaping, food truck, etc. If you don't see your industry, try another keyword or skip this step.",
      label: "What is Your Business Industry?",
      placeholder: "--Select your business industry--",
      errors: {
        required: "Please select a business category"
      },
      options: {
        restaurant: "Restaurant/Food Service",
        retail: "Retail Store",
        consulting: "Consulting",
        technology: "Technology/Software",
        construction: "Construction",
        healthcare: "Healthcare",
        real_estate: "Real Estate",
        transportation: "Transportation",
        manufacturing: "Manufacturing",
        education: "Education/Training",
        personal_services: "Personal Services",
        professional_services: "Professional Services",
        entertainment: "Entertainment",
        agriculture: "Agriculture",
        other: "Other"
      },
      next: "Next",
      skip: "Skip for now"
    },
    saveProgress: {
      title: "Who will be our primary contact for this new business?",
      subtitle: "This is who we'll contact if more info is needed during the filing process.",
      disclaimer: "We never give these details to other organizations except for filing purposes.",
      form: {
        firstName: {
          label: "First Name",
          placeholder: "Enter first name",
          errors: {
            required: "First name is required",
            minLength: "First name must be at least 2 characters"
          }
        },
        lastName: {
          label: "Last Name",
          placeholder: "Enter last name",
          errors: {
            required: "Last name is required",
            minLength: "Last name must be at least 2 characters"
          }
        },
        email: {
          label: "Email",
          placeholder: "Enter email address",
          errors: {
            required: "Email is required",
            format: "Please enter a valid email address"
          }
        },
        phone: {
          label: "Phone",
          placeholder: "Enter phone number",
          errors: {
            required: "Phone number is required",
            format: "Please enter a valid phone number"
          }
        }
      },
      next: "Next",
      skip: "Skip for now"
    },
    registeredAgent: {
      title: "Appoint your state-required Registered Agent",
      infoTitle: "Why this is recommended?",
      infoText: "It's a state legal requirement to appoint a Registered Agent for your business. Most Tartoro customers appoint Tartoro as their Registered Agent.",
      infoSubtitle: "Meet the legal requirements",
      subtitle: "It's a state requirement in Indiana to appoint a registered agent when you make your business official.",
      cardTitle: "Registered Agent",
      requirementBadge: "State Requirement",
      benefits: {
        address: "This is an official address where someone is appointed to receive documents from the Secretary of State.",
        digital: "All important documents will be delivered to you digitally.",
        charge: "You won't be charged until your business is formed."
      },
      options: {
        tartoro: "Appoint Tartoro as my Register Agent",
        other: "I'll appoint someone else"
      },
      pricingInfo: "You'll be charged $199 when your formation is complete and billed yearly thereafter.",
      next: "Next"
    },
    compliance: {
      title: "Meet state requirements with Worry-Free Compliance",
      subtitle: "The starter package includes 1 free year of worry-free compliance. This service is optional and renews at $199/yr.",
      whatToKnow: "What you should know",
      infoTitle: "Why this is recommended?",
      infoSubtitle: "We'll handle your annual report and any amendments for you.",
      infoText: "Get alerts and notifications about upcoming state compliance events so you don't miss a deadline.",
      benefits: {
        deadlines: "Meet the deadlines for your state-required annual report.",
        amendments: "File up to 2 yearly amendments — you just cover the state fee.",
        compliance: "If you miss a deadline, we'll help you get back into compliance."
      },
      options: {
        yes: "Yes, Keep me covered",
        no: "No, I'll handle it myself"
      },
      next: "Next"
    },
    einDocuments: {
      title: "Save money on your EIN and Business Documents",
      subtitle: "An EIN (Employer ID Number) is a 9-digit ID assigned by the IRS. It's required to open a business bank account and hire employees.",
      subtitle2: "Our customizable business documents include the service and contract agreements you'll need to run your business.",
      whatToKnow: "What you should know",
      infoTitle: "Why this is recommended?",
      documentsTitle: "Business Documents?",
      documentsDescription: "Get access to a template library of essential business documents and forms, like service agreements for web design, graphic design, employment, and more. The templates are easy to customize and designed to meet your needs.",
      titleDescription: "An EIN, also called a tax ID number, is an official registration with the IRS.We'll do all the work to get your EIN for you.",
      benefits: {
        bundle: "Bundle the EIN with our business document templates to help manage and run your business in a structured and compliant way.",
        charge: "You won't be charged for these until your business is formed."
      },
      options: {
        bundle: {
          text: "EIN & Business Documents",
          price: "$149"
        },
        ein: {
          text: "Just EIN",
          price: "$99"
        },
        documents: {
          text: "Just Business Documents",
          price: "$99"
        },
        none: "No thanks"
      },
      note: "NOTE: You'll be charged for these products when your formation is complete.",
      next: "Next"
    },
    operatingAgreement: {
      title: "Set the rules for your LLC with an operating agreement",
      subtitle: "The state often requires you to outline your LLC ownership, structure, and more to protect your limited liability status.",
      whatToKnow: "What you should know",
      infoTitle: "Why this is recommended?",
      infoSubtitle: "Set the rules for LLC",
      description: "The operating agreement outlines fundamental guidelines about your business, including it's purpose and managers.",
      benefits: {
        rules: "An operating agreement helps you set the rules for your LLC.",
        template: "We provide a customizable template for you to complete.",
        access: "Easily sign, store, and access this legally-binding document."
      },
      options: {
        add: {
          text: "Add the operating agreement",
          price: "$99"
        },
        none: "No thanks"
      },
      save: "Save"
    },
    pricing: {
      pricingTable: "Pricing Table",
      choosePackage: "Choose Your Package",
      choosePlan: "Select the plan that best fits your needs",
      filingFee: "Plus ${fee} state filing fee",
      benefits: "Benefits",
      packageSelected: "Package selected successfully!",
      starter: {
        name: "Starter",
        description: "Perfect for new businesses",
        continue: "Continue with Starter",
        benefits: {
          filingTime: "Fast filing time",
          accuracy: "100% accuracy guarantee",
          compliance: "Basic compliance support"
        }
      },
      pro: {
        name: "Pro",
        description: "Best for growing businesses",
        continue: "Continue with Pro",
        benefits: {
          filingTime: "Priority filing time",
          accuracy: "100% accuracy guarantee",
          compliance: "Enhanced compliance support",
          operatingAgreement: "Custom operating agreement",
          ein: "EIN application service"
        }
      },
      premium: {
        name: "Premium",
        description: "Complete business solution",
        continue: "Continue with Premium",
        benefits: {
          proPackage: "Everything in Pro package",
          templates: "Business document templates",
          websiteBuilder: "Website builder",
          domain: "Free domain name",
          email: "Business email setup"
        }
      }
    },
    customerSignIn: {
      welcome: {
        title: "Welcome to Tartoro. Your foundation for success",
        description: "Form, transition, and manage your LLC effortlessly. We make business simple, so you have more time for what matters.",
        features: {
          secure: "Secure – We keep your information safe.",
          easy: "Easy – Setting up and managing your LLC is quick and simple.",
          support: "Support – We're here to help whenever you need it."
        }
      },
      form: {
        title: "Sign in",
        email: {
          label: "Email",
          placeholder: "Enter email"
        },
        password: {
          label: "Password",
          placeholder: "Enter Password"
        },
        forgotPassword: "Forgot password?",
        terms: {
          text: "By selecting Continue, you agree to our",
          termsOfService: "Terms of Service",
          and: "and acknowledge our",
          privacyPolicy: "Privacy Policy"
        },
        submit: "Sign In",
        loading: "Signing in..."
      },
      errors: {
        termsRequired: "You must accept the terms to continue.",
        signInFailed: "Sign in failed. Please try again."
      }
    },
    otpVerification: {
      title: "Verify your identity",
      description: "Enter the 4-digit code sent to your email to continue. This helps us keep your account secure.",
      form: {
        title: "OTP Verification",
        label: "Enter 4-digit code",
        verify: "Verify",
        verifying: "Verifying...",
        resend: "Didn't receive the code?",
        resendLink: "Resend"
      },
      errors: {
        incomplete: "Please enter the 4-digit code.",
        verificationFailed: "Verification failed. Please try again.",
        resendFailed: "Failed to resend code. Please try again."
      }
    },
    customerDashboard: {
      uspsAlert: {
        title: "Action Required: USPS Form 1583",
        message: "Please complete Form 1583 to activate your mail forwarding service.",
        button: "Complete Form"
      },
      comingSoon: {
        title: "Coming Soon",
        message: "This feature is currently under development and will be available soon.",
        button: "Close"
      },
      welcome: "Welcome to {businessName}",
      progress: {
        title: "Business Formation Progress",
        steps: {
          application: {
            title: "Application Submitted on",
            date: "{date}"
          },
          ra: {
            title: "Registered Agent Assigned",
            status: {
              assigned: "Assigned",
              pending: "Pending"
            }
          },
          llc: {
            title: "LLC Registered",
            status: {
              inProgress: "In Progress",
              pending: "Pending"
            }
          },
          ein: {
            title: "EIN Received",
            status: {
              completed: "Completed",
              pending: "Pending"
            }
          }
        }
      },
      quickActions: {
        title: "Quick Actions",
        articles: {
          title: "Articles of Organization",
          description: "Download your official documents.",
          button: "Download"
        },
        receipt: {
          title: "State Filing Receipt",
          description: "Download your official documents.",
          button: "Download"
        },
        fein: {
          title: "FEIN Details",
          description: "Download your official documents.",
          button: "Check Status"
        }
      }
    },
    adminLogin: {
      welcome: {
        title: "Welcome to Admin Portal",
        subtitle: "Manage your organization's data and settings with our intuitive dashboard.",
        features: {
          secure: "Secure access to your organization's data",
          tools: "Streamlined administration tools",
          analytics: "Real-time analytics and insights"
        }
      },
      form: {
        title: "Admin Login",
        subtitle: "Enter your credentials to access the dashboard",
        email: {
          label: "Email",
          placeholder: "Enter your email"
        },
        password: {
          label: "Password",
          placeholder: "Enter your password"
        },
        rememberMe: "Remember me",
        submit: "Login",
        loading: "Logging in..."
      },
      support: {
        text: "Having trouble?",
        link: "Contact support",
        modal: {
          title: "Contact Support",
          company: "Tartoro",
          founder: {
            name: "David C. Morales",
            title: "Founder | Business Development",
            phone: "Cellular: 385-556-8823",
            email: "Email: support@tartoro.com"
          },
          close: "Close"
        }
      },
      errors: {
        loginFailed: "Login failed. Please try again."
      }
    },
    agentLogin: {
      welcome: {
        title: "Welcome to Agent Portal",
        subtitle: "Access your agent dashboard and manage client accounts efficiently.",
        features: {
          manage: "Manage multiple client accounts",
          track: "Track client progress and status",
          documents: "Access client documents and information"
        }
      },
      form: {
        title: "Agent Login",
        subtitle: "Enter your credentials to access the agent dashboard",
        email: {
          label: "Email",
          placeholder: "Enter your email"
        },
        password: {
          label: "Password",
          placeholder: "Enter your password"
        },
        rememberMe: "Remember me",
        submit: "Login",
        loading: "Logging in..."
      },
      support: {
        text: "Having trouble?",
        link: "Contact support",
        modal: {
          title: "Contact Support",
          company: "Tartoro",
          founder: {
            name: "David C. Morales",
            title: "Founder | Business Development",
            phone: "Cellular: 385-556-8823",
            email: "Email: support@tartoro.com"
          },
          close: "Close"
        }
      },
      errors: {
        loginFailed: "Login failed. Please try again."
      }
    },
    forgotPassword: {
      welcome: {
        title: "Forgot Your Password?",
        description: "Don't worry! Enter your email address below and we'll send you a code to reset your password.",
        features: {
          secure: "Seguro – Mantenemos tu información segura.",
          easy: "Fácil – Configurar y administrar tu LLC es rápido y sencillo.",
          support: "Soporte – Estamos aquí para ayudarte cuando lo necesites."
        }
      },
      form: {
        email: {
          label: "Email Address",
          placeholder: "Enter your email address"
        },
        otp: {
          label: "Verification Code",
          placeholder: "Enter the 6-digit code"
        },
        requestOTP: "Send Verification Code",
        verifyOTP: "Verify Code",
        loading: "Processing...",
        otpSent: "A verification code has been sent to your email.",
        resend: "Didn't receive the code?",
        resendLink: "Resend"
      },
      errors: {
        requestFailed: "Failed to send verification code. Please try again.",
        verificationFailed: "Invalid verification code. Please try again."
      }
    },
    changePassword: {
      welcome: {
        title: "Create New Password",
        description: "Please create a strong password to protect your account.",
        features: {
          secure: "Secure – Create a strong password to protect your account.",
          easy: "Easy – Choose a password you'll remember.",
          support: "Support – We're here to help whenever you need it."
        }
      },
      form: {
        newPassword: {
          label: "New Password",
          placeholder: "Enter new password"
        },
        confirmPassword: {
          label: "Confirm Password",
          placeholder: "Confirm new password"
        },
        submit: "Reset Password",
        loading: "Processing..."
      },
      errors: {
        passwordMismatch: "Passwords do not match",
        passwordTooShort: "Password must be at least 8 characters long",
        noEmailFound: "Session expired. Please try again.",
        resetFailed: "Failed to reset password. Please try again."
      }
    },
    raDashboard: {
      welcome: {
        title: "Welcome, {name}",
        subtitle: "Registered Agent Portal"
      },
      stats: {
        totalBusinesses: {
          title: "Total Businesses",
          noNew: "No new businesses this month",
          new: "+{count} this month"
        },
        requestedMail: {
          title: "Requested Mail",
          noPending: "No pending mail",
          pending: "{count} pending requests"
        },
        completedMail: {
          title: "Completed Mail",
          noNew: "No new completions",
          new: "+{count} this month"
        },
        totalDocuments: {
          title: "Total Documents",
          noNew: "No new documents this month",
          new: "+{count} this month"
        }
      },
      activity: {
        title: "Recent Activity",
        viewAll: "View All",
        loading: "Loading activities...",
        retry: "Retry",
        noActivities: "No recent activities",
        allActivities: "All Activities",
        close: "Close"
      },
      nav: {
        mainMenu: "MAIN MENU",
        dashboard: "Dashboard",
        business: "Business",
        incomingMail: "Incoming Mail",
        mailRequest: "Mail Request",
        accountManagement: "ACCOUNT MANAGEMENT",
        settings: "Settings"
      },
      language: {
        english: "English",
        spanish: "Español"
      },
      user: {
        defaultName: "Agent User",
        role: "Registered Agent"
      },
      errors: {
        loadFailed: "Failed to load agent data",
        dashboardLoadFailed: "Failed to load dashboard data",
        activitiesLoadFailed: "Failed to load activities"
      }
    },
    raBusiness: {
      search: {
        placeholder: "Search businesses..."
      },
      loading: "Loading businesses...",
      table: {
        businessName: "Business Name",
        tradeName: "Trade Name",
        state: "State",
        status: "Status",
        ein: "EIN",
        createdDate: "Created Date",
        actions: "Actions"
      },
      actions: {
        view: "View"
      },
      pending: "Pending",
      noSearchResults: "No businesses found matching your search",
      noBusinesses: "No businesses assigned yet"
    },
    raIncomingMail: {
      search: {
        placeholder: "Search mail..."
      },
      loading: "Loading incoming mail...",
      table: {
        business: "Business",
        type: "Type",
        priority: "Priority",
        status: "Status",
        received: "Received",
        description: "Description",
        actions: "Actions"
      },
      actions: {
        upload: "Upload Mail",
        view: "View",
        download: "Download"
      }, mailTypes: {
        legal: "Legal",
        tax: "Tax",
        general: "General",
        other: "Other"
      },
      priorities: {
        low: "Low",
        medium: "Medium",
        high: "High",
        urgent: "Urgent"
      },
      statuses: {
        received: "Received",
        processed: "Processed",
        forwarded: "Forwarded",
        shredded: "Shredded"
      },
      viewModal: {
        title: "Mail Details",
        business: "Business",
        type: "Mail Type",
        priority: "Priority",
        status: "Status",
        received: "Received Date",
        description: "Description",
        attachments: "Attachments"
      },
      upload: {
        title: "Upload Incoming Mail",
        form: {
          business: {
            label: "Business",
            placeholder: "Select Business"
          },
          mailType: {
            label: "Mail Type",
            placeholder: "Select Mail Type"
          },
          priority: {
            label: "Priority",
            placeholder: "Select Priority"
          },
          receivedDate: {
            label: "Received Date"
          },
          description: {
            label: "Description",
            placeholder: "Enter any additional notes or description"
          },
          attachments: {
            label: "Attachments",
            dragDrop: {
              title: "Drag and drop files here",
              or: "or",
              browse: "browse files",
              hint: "JPEG, PNG, PDF and DOC files up to 5MB"
            }
          }
        },
        validation: {
          required: "This field is required",
          fileType: "{fileName}: Invalid file type. Only JPEG, PNG, PDF and DOC files are allowed.",
          fileSize: "{fileName}: File size exceeds 5MB limit."
        },
        buttons: {
          cancel: "Cancel",
          upload: "Upload",
          uploading: "Uploading..."
        }
      },
      noSearchResults: "No mail found matching your search",
      noMail: "No incoming mail yet",
      unnamedBusiness: "Unnamed Business",
      errors: {
        fetchFailed: "Failed to fetch incoming mail",
        downloadFailed: "Failed to download attachment",
        uploadFailed: "Failed to upload mail"
      }
    },
    raBusinessDetails: {
      title: "Business Details",
      sections: {
        businessInfo: {
          title: "Business & Contact Information",
          fields: {
            businessName: "Business Name",
            tradeName: "Trade Name",
            entityType: "Entity Type",
            businessCategory: "Business Category",
            businessAddress: "Business Address",
            state: "State",
            zipCode: "ZIP Code",
            status: "Status",
            ein: "EIN"
          }
        },
        responsibleParty: {
          title: "Responsible Party",
          fields: {
            name: "Name",
            role: "Role",
            ssn: "SSN",
            email: "Email",
            phone: "Phone"
          }
        },
        businessOperations: {
          title: "Business Info",
          fields: {
            experience: "Experience",
            employeeCount: "Employee Count",
            progress: "Progress",
            goals: "Goals",
            activity: "Activity",
            products: "Products",
            startDate: "Start Date"
          }
        },
        compliance: {
          title: "Compliance & Operations",
          fields: {
            willHireEmployees: "Will Hire Employees",
            expectedEmployees: "Expected Employees",
            taxReturnsType: "Tax Returns Type",
            yearEndDate: "Year End Date"
          }
        },
        packageInfo: {
          title: "Package Info",
          fields: {
            selectedPackage: "Selected Package",
            packageStatus: "Package Status",
            startDate: "Start Date",
            endDate: "End Date",
            price: "Price",
            billingCycle: "Billing Cycle",
            autoRenew: "Auto Renew"
          }
        },
        otherInfo: {
          title: "Other Info",
          fields: {
            registeredAgent: "Registered Agent",
            agentId: "Agent ID",
            customerId: "Customer ID"
          }
        }
      },
      navigation: {
        businesses: "Businesses",
        details: "Details"
      },
      states: {
        loading: "Loading business details...",
        error: "Failed to load business details"
      }
    },
    customerHome: {
      nav: {
        informationManagement: "INFORMATION MANAGEMENT",
        companyManagement: "COMPANY MANAGEMENT",
        accountManagement: "ACCOUNT MANAGEMENT",
        dashboard: "Dashboard",
        businessDetails: "Business Details",
        documents: "Documents",
        mail: "Mail",
        users: "Users",
        settings: "Settings"
      },
      header: {
        selectBusiness: "Select Business",
        noBusinesses: "No businesses available"
      },
      sidebar: {
        toggle: "Toggle Sidebar"
      },
      user: {
        logout: "Logout"
      }
    },
    customerBusinessDetails: {
      title: "Business Details",
      sections: {
        businessInfo: {
          title: "Business & Contact Information",
          fields: {
            legalBusinessName: "Legal Business Name",
            tradeName: "Trade Name",
            entityType: "Entity Type",
            businessCategory: "Business Category",
            ein: "EIN Number",
            state: "State of Formation",
            startDate: "Start Date",
            declarationDate: "Declaration Date",
            businessAddress: "Business Address",
            zipCode: "ZIP Code",
            phoneNumber: "Phone Number",
            emailAddress: "Email Address"
          }
        }
      },
      buttons: {
        edit: "Edit",
        save: "Save"
      },
      notSpecified: "Not specified"
    },
    customerDocuments: {
      categories: {
        all: "All Documents",
        formation: "Formation Documents",
        tax: "Tax Documents",
        compliance: "Compliance"
      },
      viewModes: {
        list: "List View",
        grid: "Grid View"
      },
      table: {
        headers: {
          name: "Document Name",
          description: "Description",
          category: "Category",
          date: "Date",
          actions: "Actions"
        }
      },
      actions: {
        view: "View",
        download: "Download"
      },
      upload: {
        title: "Upload New Document",
        dragDrop: "Drag and drop your files here or click to browse"
      },
      documents: {
        articles: {
          name: "Articles of Organization",
          description: "LLC Formation Document"
        },
        receipt: {
          name: "State Filing Receipt",
          description: "Filing Receipt"
        },
        fein: {
          name: "FEIN Details",
          description: "Federal Tax ID"
        }
      }
    },
    customerMail: {
      search: {
        placeholder: 'Search mail...'
      },
      controls: {
        filter: 'Filter',
        selectMail: 'Select Mail',
        viewList: 'List View',
        viewGrid: 'Grid View'
      },
      loading: 'Loading mail items...',
      empty: {
        title: 'No mail items found'
      },
      table: {
        headers: {
          id: 'ID',
          type: 'Type',
          priority: 'Priority',
          receivedDate: 'Received Date',
          status: 'Status',
          tracking: 'Tracking',
          actions: 'Actions',
          description: 'Description'
        }
      },
      status: {
        received: 'Received',
        scan_requested: 'Scan Requested',
        scanned: 'Scanned',
        forward_requested: 'Forward Requested',
        forwarded: 'Forwarded',
        shred_requested: 'Shred Requested',
        shredded: 'Shredded'
      },
      actions: {
        view: 'View',
        forward: 'Forward',
        shred: 'Shred',
        scanRequest: 'Request Scan'
      },
      preview: {
        title: 'Mail Preview',
        attachments: 'Attachments',
        noAttachments: 'No attachments available',
        preview: {
          loading: 'Loading preview...',
          title: 'Preview',
          unsupported: 'Preview not available for this file type',
          downloadToView: 'Download to View'
        },
        actions: {
          zoomIn: 'Zoom In',
          zoomOut: 'Zoom Out',
          resetZoom: 'Reset Zoom',
          download: 'Download'
        }
      }
    },
    customerUsersList: {
      title: "Users & Permissions",
      addUser: "Add User",
      viewModes: {
        list: "List",
        grid: "Grid"
      },
      teamMembers: "Team Members",
      search: {
        placeholder: "Search users..."
      },
      loading: "Loading users...",
      error: {
        message: "Failed to load users. Please try again.",
        retry: "Retry"
      },
      empty: {
        title: "No users found for this business.",
        addUser: "Add User"
      },
      table: {
        headers: {
          name: "Name",
          email: "Email",
          role: "Role",
          status: "Status",
          actions: "Actions"
        }
      },
      roles: {
        employee: "Employee",
        businessOwner: "Business Owner"
      },
      status: {
        active: "Active",
        inactive: "Inactive"
      },
      actions: {
        edit: "Edit"
      },
      modals: {
        addUser: {
          title: "Add New User",
          form: {
            firstName: "First Name",
            lastName: "Last Name",
            email: "Email",
            phone: "Phone",
            role: "Role",
            status: "Status"
          },
          buttons: {
            cancel: "Cancel",
            submit: "Add User"
          }
        },
        editUser: {
          title: "Edit User",
          form: {
            firstName: "First Name",
            lastName: "Last Name",
            email: "Email",
            phone: "Phone",
            role: "Role",
            status: "Status"
          },
          buttons: {
            cancel: "Cancel",
            submit: "Update User"
          }
        }
      },
      rolesAndPermissions: {
        title: "Roles & Permissions"
      },
      notifications: {
        userAdded: "User added successfully. Default password is 1234",
        userUpdated: "User updated successfully",
        error: "Failed to update user. Please try again."
      }
    },
    customerSettings: {
      title: "Settings",
      loading: "Loading...",
      tabs: {
        profile: "Profile",
        security: "Security",
        notifications: "Notifications",
        package: "Package",
        danger: "Danger Zone"
      },
      profile: {
        title: "Profile Settings",
        avatar: {
          changePhoto: "Change Photo"
        },
        form: {
          fullName: "Full Name",
          email: "Email",
          phoneNumber: "Phone Number",
          saveChanges: "Save Changes"
        }
      },
      security: {
        title: "Security Settings",
        form: {
          currentPassword: "Current Password",
          newPassword: "New Password",
          confirmPassword: "Confirm New Password",
          updatePassword: "Update Password",
          updating: "Updating..."
        },
        errors: {
          required: "All password fields are required",
          mismatch: "New passwords do not match",
          tooShort: "New password must be at least 8 characters long"
        },
        success: "Password updated successfully"
      },
      notifications: {
        title: "Notification Settings",
        email: {
          title: "Email Notifications",
          subtitle: "Choose what emails you'd like to receive",
          updates: "Product updates and announcements",
          security: "Security alerts",
          newsletter: "Newsletter"
        },
        push: {
          title: "Push Notifications",
          subtitle: "Manage your mobile notifications",
          updates: "New updates",
          security: "Security alerts"
        }
      },
      package: {
        title: "Package & Additional Information",
        sections: {
          packageDetails: {
            title: "Package Details",
            selectedPackage: "Selected Package",
            packageStatus: "Package Status",
            startDate: "Start Date",
            endDate: "End Date",
            status: {
              select: "Select Status",
              active: "Active",
              pending: "Pending",
              expired: "Expired"
            }
          },
          businessInfo: {
            title: "Business Information",
            activity: "Business Activity",
            employeeCount: "Employee Count",
            progress: "Business Progress",
            goals: "Business Goals",
            progressOptions: {
              select: "Select Progress",
              startup: "Startup",
              growing: "Growing",
              established: "Established"
            }
          },
          responsibleParty: {
            title: "Responsible Party Information",
            name: "Responsible Party Name",
            role: "Role",
            experience: "Business Experience",
            reason: "Reason for Applying",
            experienceOptions: {
              select: "Select Experience",
              beginner: "Beginner (0-2 years)",
              intermediate: "Intermediate (2-5 years)",
              expert: "Expert (5+ years)"
            }
          }
        },
        saveButton: "Save Package Info"
      },
      dangerZone: {
        title: "Danger Zone",
        deleteAccount: {
          title: "Delete Account",
          description: "Permanently delete your account and all data",
          button: "Delete Account"
        }
      }
    },
    raMailRequest: {
      search: {
        placeholder: 'Search mail...'
      },
      filters: {
        dateRange: {
          label: 'Date Range',
          allTime: 'All Time',
          today: 'Today',
          week: 'This Week',
          month: 'This Month'
        }
      },
      tabs: {
        requested: 'Requested',
        completed: 'Completed'
      },
      viewToggle: {
        grid: 'Grid View',
        table: 'Table View'
      },
      states: {
        loading: 'Loading mail requests...',
        retry: 'Retry',
        empty: 'No mail requests found',
        clearFilters: 'Clear Filters'
      },
      table: {
        id: 'ID',
        status: 'Status',
        receivedDate: 'Received Date',
        business: 'Business',
        type: 'Type',
        description: 'Description',
        action: 'Action'
      },
      status: {
        received: 'Received',
        scan_requested: 'Scan Requested',
        scanned: 'Scanned',
        forward_requested: 'Forward Requested',
        forwarded: 'Forwarded',
        shred_requested: 'Shred Requested',
        shredded: 'Shredded'
      },
      actions: {
        scan: 'Scan',
        forward: 'Forward',
        shred: 'Shred',
        view: 'View'
      },
      mobile: {
        filters: 'Filters',
        clear: 'Clear',
        apply: 'Apply'
      }
    },
    raSettings: {
      title: "Settings",
      tabs: {
        profile: "Profile",
        security: "Security",
        notifications: "Notifications",
        preferences: "Preferences"
      },
      profile: {
        title: "Profile Settings",
        avatar: {
          changePhoto: "Change Photo",
          removePhoto: "Remove Photo"
        },
        form: {
          firstName: {
            label: "First Name",
            placeholder: "Enter first name"
          },
          lastName: {
            label: "Last Name",
            placeholder: "Enter last name"
          },
          email: {
            label: "Email",
            placeholder: "Enter email address"
          },
          phone: {
            label: "Phone",
            placeholder: "Enter phone number"
          },
          role: {
            label: "Role",
            value: "Registered Agent"
          },
          saveChanges: "Save Changes",
          saving: "Saving..."
        }
      },
      security: {
        title: "Security Settings",
        form: {
          currentPassword: {
            label: "Current Password",
            placeholder: "Enter current password"
          },
          newPassword: {
            label: "New Password",
            placeholder: "Enter new password"
          },
          confirmPassword: {
            label: "Confirm Password",
            placeholder: "Confirm new password"
          },
          updatePassword: "Update Password",
          updating: "Updating..."
        },
        errors: {
          required: "All password fields are required",
          mismatch: "New passwords do not match",
          tooShort: "New password must be at least 8 characters long"
        },
        success: "Password updated successfully"
      },
      notifications: {
        title: "Notification Settings",
        email: {
          title: "Email Notifications",
          subtitle: "Choose what emails you'd like to receive",
          newMail: "New mail received",
          mailUpdates: "Mail status updates",
          businessUpdates: "Business updates",
          systemUpdates: "System updates"
        },
        push: {
          title: "Push Notifications",
          subtitle: "Manage your mobile notifications",
          newMail: "New mail received",
          mailUpdates: "Mail status updates",
          businessUpdates: "Business updates"
        }
      },
      preferences: {
        title: "Preferences",
        language: {
          label: "Language",
          options: {
            en: "English",
            es: "Español"
          }
        },
        theme: {
          label: "Theme",
          options: {
            light: "Light",
            dark: "Dark",
            system: "System"
          }
        },
        timezone: {
          label: "Timezone",
          placeholder: "Select timezone"
        },
        dateFormat: {
          label: "Date Format",
          options: {
            mmddyyyy: "MM/DD/YYYY",
            ddmmyyyy: "DD/MM/YYYY",
            yyyymmdd: "YYYY/MM/DD"
          }
        }
      },
      states: {
        loading: "Loading settings...",
        saving: "Saving changes...",
        error: "Failed to save changes",
        success: "Changes saved successfully"
      }
    }
  },
  es: {
    common: {
      back: "Atrás"
    },
    businessDetails: {
      title: "Comencemos con los detalles de tu negocio",
      fast: "rápido",
      with: "con",
      subtitle: "El primer paso para perseguir tu pasión comienza aquí.",
      businessName: {
        label: "¿Cómo te gustaría nombrar tu LLC?",
        placeholder: "Nombre del negocio (ej., Tartoro LLC)",
        errors: {
          required: "El nombre del negocio es requerido",
          minLength: "El nombre del negocio debe tener al menos 2 caracteres",
          maxLength: "El nombre del negocio no puede exceder 100 caracteres",
          format: "El nombre del negocio solo puede contener letras, números, espacios y puntuación básica"
        }
      },
      alternativeName: {
        label: "Nombre alternativo de LLC (si la primera opción no está disponible)",
        placeholder: "Opcional: Solo si tu nombre preferido está tomado"
      },
      formationState: {
        label: "Elige el Estado de Formación",
        placeholder: "Selecciona el estado de formación de tu LLC",
        errors: {
          required: "Por favor selecciona un estado de formación"
        }
      },
      next: "Siguiente"
    },
    businessExperience: {
      title: "¿Cuánta experiencia tienes administrando un negocio?",
      options: {
        none: "Ninguna",
        some: "Alguna",
        lots: "Mucha"
      },
      next: "Siguiente",
      skip: "Omitir por ahora"
    },
    businessEmployees: {
      title: "¿Tu negocio tendrá empleados en el primer año?",
      options: {
        none: "No, solo yo",
        few: "Sí (1-5)",
        many: "Sí (6+)"
      },
      next: "Siguiente",
      skip: "Omitir por ahora"
    },
    businessAddress: {
      title: "¿La dirección de tu negocio será diferente de tu dirección personal?",
      options: {
        no: "No",
        yes: "Sí"
      },
      form: {
        street: {
          label: "Dirección",
          placeholder: "Ingresa la dirección"
        },
        city: {
          label: "Ciudad",
          placeholder: "Ingresa la ciudad"
        },
        state: {
          label: "Estado",
          placeholder: "Selecciona el estado"
        },
        zipCode: {
          label: "Código Postal",
          placeholder: "Ingresa el código postal"
        }
      },
      next: "Siguiente",
      skip: "Omitir por ahora"
    },
    businessProgress: {
      title: "¿Qué tan avanzado está tu negocio?",
      options: {
        launching: "Lanzamiento Próximo (1-2 meses)",
        making_money: "Ya está generando ingresos",
        planning: "Planificación para el futuro"
      },
      next: "Siguiente",
      skip: "Omitir por ahora"
    },
    businessGoals: {
      title: "¿Cuáles son tus objetivos de negocio?",
      options: {
        side: "Negocio secundario",
        primary: "Ingreso principal",
        next_big: "El próximo gran proyecto"
      },
      next: "Siguiente",
      skip: "Omitir por ahora"
    },
    businessCategory: {
      title: "¿Qué tipo de negocio estás formando?",
      subtitle: "Ahora, elige una categoría para tu negocio. Por ejemplo: jardinería, food truck, etc. Si no ves tu industria, intenta con otra palabra clave o omite este paso.",
      label: "¿Cuál es tu industria?",
      placeholder: "--Selecciona tu industria--",
      errors: {
        required: "Por favor selecciona una categoría de negocio"
      },
      options: {
        restaurant: "Restaurante/Servicio de Alimentos",
        retail: "Tienda Minorista",
        consulting: "Consultoría",
        technology: "Tecnología/Software",
        construction: "Construcción",
        healthcare: "Salud",
        real_estate: "Bienes Raíces",
        transportation: "Transporte",
        manufacturing: "Manufactura",
        education: "Educación/Entrenamiento",
        personal_services: "Servicios Personales",
        professional_services: "Servicios Profesionales",
        entertainment: "Entretenimiento",
        agriculture: "Agricultura",
        other: "Otro"
      },
      next: "Siguiente",
      skip: "Omitir por ahora"
    },
    saveProgress: {
      title: "¿Quién será nuestro contacto principal para este nuevo negocio?",
      subtitle: "Esta es la persona a la que contactaremos si necesitamos más información durante el proceso de presentación.",
      disclaimer: "Jamás compartiremos estos detalles con otras organizaciones, excepto para fines de presentación.",
      form: {
        firstName: {
          label: "Nombre",
          placeholder: "Ingresa el nombre",
          errors: {
            required: "El nombre es requerido",
            minLength: "El nombre debe tener al menos 2 caracteres"
          }
        },
        lastName: {
          label: "Apellido",
          placeholder: "Ingresa el apellido",
          errors: {
            required: "El apellido es requerido",
            minLength: "El apellido debe tener al menos 2 caracteres"
          }
        },
        email: {
          label: "Correo Electrónico",
          placeholder: "Ingresa el correo electrónico",
          errors: {
            required: "El correo electrónico es requerido",
            format: "Por favor ingresa un correo electrónico válido"
          }
        },
        phone: {
          label: "Teléfono",
          placeholder: "Ingresa el número de teléfono",
          errors: {
            required: "El número de teléfono es requerido",
            format: "Por favor ingresa un número de teléfono válido"
          }
        }
      },
      next: "Siguiente",
      skip: "Omitir por ahora"
    },
    registeredAgent: {
      title: "Nombra tu Agente Registrado requerido por el estado",
      subtitle: "Es un requisito del estado en Indiana nombrar un agente registrado cuando haces oficial tu negocio.",
      cardTitle: "Agente Registrado",
      requirementBadge: "Requisito del Estado",
      benefits: {
        address: "Esta es una dirección oficial donde alguien es nombrado para recibir documentos del Secretario de Estado.",
        digital: "Todos los documentos importantes te serán entregados digitalmente.",
        charge: "No se te cobrará hasta que tu negocio esté formado."
      },
      options: {
        tartoro: "Nombrar a Tartoro como mi Agente Registrado",
        other: "Nombraré a alguien más"
      },
      pricingInfo: "Se te cobrará $199 cuando tu formación esté completa y se facturará anualmente a partir de entonces.",
      next: "Siguiente"
    },
    compliance: {
      title: "Cumple con los requisitos estatales con Cumplimiento Sin Preocupaciones",
      subtitle: "El paquete inicial incluye 1 año gratis de cumplimiento sin preocupaciones. Este servicio es opcional y se renueva a $199/año.",
      whatToKnow: "Lo que debes saber",
      benefits: {
        deadlines: "Cumple con los plazos para tu informe anual requerido por el estado.",
        amendments: "Presenta hasta 2 enmiendas anuales — solo cubres la tarifa estatal.",
        compliance: "Si pierdes un plazo, te ayudaremos a volver al cumplimiento."
      },
      options: {
        yes: "Sí, mantenme cubierto",
        no: "No, lo manejaré yo mismo"
      },
      next: "Siguiente"
    },
    einDocuments: {
      title: "Ahorra dinero en tu EIN y Documentos de Negocio",
      subtitle: "Un EIN (Número de Identificación del Empleador) es un ID de 9 dígitos asignado por el IRS. Es necesario para abrir una cuenta bancaria comercial y contratar empleados.",
      subtitle2: "Nuestros documentos comerciales personalizables incluyen los acuerdos de servicio y contratos que necesitarás para administrar tu negocio.",
      whatToKnow: "Lo que debes saber",
      benefits: {
        bundle: "Agrupa el EIN con nuestras plantillas de documentos comerciales para ayudar a administrar y ejecutar tu negocio de manera estructurada y conforme.",
        charge: "No se te cobrará por estos hasta que tu negocio esté formado."
      },
      options: {
        bundle: {
          text: "EIN y Documentos Comerciales",
          price: "$149"
        },
        ein: {
          text: "Solo EIN",
          price: "$99"
        },
        documents: {
          text: "Solo Documentos Comerciales",
          price: "$99"
        },
        none: "No, gracias"
      },
      note: "NOTA: Se te cobrará por estos productos cuando tu formación esté completa.",
      next: "Siguiente"
    },
    operatingAgreement: {
      title: "Establece las reglas para tu LLC con un acuerdo operativo",
      subtitle: "El estado a menudo requiere que describas la propiedad, estructura y más de tu LLC para proteger tu estado de responsabilidad limitada.",
      whatToKnow: "Lo que debes saber",
      benefits: {
        rules: "Un acuerdo operativo te ayuda a establecer las reglas para tu LLC.",
        template: "Proporcionamos una plantilla personalizable para que la completes.",
        access: "Firma, almacena y accede fácilmente a este documento legalmente vinculante."
      },
      options: {
        add: {
          text: "Agregar el acuerdo operativo",
          price: "$99"
        },
        none: "No, gracias"
      },
      save: "Guardar"
    },
    pricing: {
      pricingTable: "Tabla de Precios",
      choosePackage: "Elige Tu Paquete",
      choosePlan: "Selecciona el plan que mejor se adapte a tus necesidades",
      filingFee: "Más ${fee} cargo de presentación estatal",
      benefits: "Beneficios",
      packageSelected: "Paquete seleccionado exitosamente!",
      starter: {
        name: "Principiante",
        description: "Perfecto para nuevos negocios",
        continue: "Continuar con Principiante",
        benefits: {
          filingTime: "Tiempo de presentación rápido",
          accuracy: "Garantía de precisión del 100%",
          compliance: "Soporte de cumplimiento básico"
        }
      },
      pro: {
        name: "Pro",
        description: "Mejor para negocios en crecimiento",
        continue: "Continuar con Pro",
        benefits: {
          filingTime: "Tiempo de presentación prioritario",
          accuracy: "Garantía de precisión del 100%",
          compliance: "Soporte de cumplimiento mejorado",
          operatingAgreement: "Acuerdo operativo personalizado",
          ein: "Servicio de aplicación del EIN"
        }
      },
      premium: {
        name: "Premium",
        description: "Solución completa de negocio",
        continue: "Continuar con Premium",
        benefits: {
          proPackage: "Todo en el paquete Pro",
          templates: "Plantillas de documentos de negocio",
          websiteBuilder: "Constructor de sitios web",
          domain: "Nombre de dominio gratis",
          email: "Configuración de correo electrónico de negocio"
        }
      }
    },
    customerSignIn: {
      welcome: {
        title: "Bienvenido a Tartoro. Tu base para el éxito",
        description: "Forma, transiciona y administra tu LLC sin esfuerzo. Hacemos que los negocios sean simples, para que tengas más tiempo para lo que importa.",
        features: {
          secure: "Seguro – Mantenemos tu información segura.",
          easy: "Fácil – Configurar y administrar tu LLC es rápido y sencillo.",
          support: "Soporte – Estamos aquí para ayudarte cuando lo necesites."
        }
      },
      form: {
        title: "Iniciar sesión",
        email: {
          label: "Correo electrónico",
          placeholder: "Ingrese su correo electrónico"
        },
        password: {
          label: "Contraseña",
          placeholder: "Ingrese su contraseña"
        },
        forgotPassword: "¿Olvidó su contraseña?",
        terms: {
          text: "Al seleccionar Continuar, acepta nuestros",
          termsOfService: "Términos de Servicio",
          and: "y reconoce nuestra",
          privacyPolicy: "Política de Privacidad"
        },
        submit: "Iniciar sesión",
        loading: "Iniciando sesión..."
      },
      errors: {
        termsRequired: "Debe aceptar los términos para continuar.",
        signInFailed: "Error al iniciar sesión. Por favor, inténtelo de nuevo."
      }
    },
    otpVerification: {
      title: "Verifica tu identidad",
      description: "Ingresa el código de 4 dígitos enviado a tu correo electrónico para continuar. Esto nos ayuda a mantener tu cuenta segura.",
      form: {
        title: "Verificación OTP",
        label: "Ingresa el código de 4 dígitos",
        verify: "Verificar",
        verifying: "Verificando...",
        resend: "¿No recibiste el código?",
        resendLink: "Reenviar"
      },
      errors: {
        incomplete: "Por favor ingresa el código de 4 dígitos.",
        verificationFailed: "La verificación falló. Por favor intenta de nuevo.",
        resendFailed: "Error al reenviar el código. Por favor intenta de nuevo."
      }
    },
    customerDashboard: {
      uspsAlert: {
        title: "Accion Requerida: Formulario 1583 de USPS",
        message: "Por favor complete el Formulario 1583 para activar su servicio de reenvío de correo.",
        button: "Completar Formulario"
      },
      comingSoon: {
        title: "Próximamente",
        message: "Esta función está actualmente en desarrollo y estará disponible pronto.",
        button: "Cerrar"
      },
      welcome: "Bienvenido a {businessName}",
      progress: {
        title: "Progreso de Formación del Negocio",
        steps: {
          application: {
            title: "Solicitud Enviada el",
            date: "{date}"
          },
          ra: {
            title: "Agente Registrado Asignado",
            status: {
              assigned: "Asignado",
              pending: "Pendiente"
            }
          },
          llc: {
            title: "LLC Registrada",
            status: {
              inProgress: "En progreso",
              pending: "Pendiente"
            }
          },
          ein: {
            title: "EIN Recibido",
            status: {
              completed: "Completado",
              pending: "Pendiente"
            }
          }
        }
      },
      quickActions: {
        title: "Acciones Rápidas",
        articles: {
          title: "Artículos de Organización",
          description: "Descargue sus documentos oficiales.",
          button: "Descargar"
        },
        receipt: {
          title: "Recibo de presentación estatal",
          description: "Descargue sus documentos oficiales.",
          button: "Descargar"
        },
        fein: {
          title: "Detalles del FEIN",
          description: "Descargue sus documentos oficiales.",
          button: "Verificar Estado"
        }
      }
    },
    adminLogin: {
      welcome: {
        title: "Bienvenido al Portal de Administración",
        subtitle: "Administre los datos y la configuración de su organización con nuestro panel intuitivo.",
        features: {
          secure: "Acceso seguro a los datos de su organización",
          tools: "Herramientas de administración optimizadas",
          analytics: "Análisis e información en tiempo real"
        }
      },
      form: {
        title: "Inicio de Sesión de Administrador",
        subtitle: "Ingrese sus credenciales para acceder al panel",
        email: {
          label: "Correo Electrónico",
          placeholder: "Ingrese su correo electrónico"
        },
        password: {
          label: "Contraseña",
          placeholder: "Ingrese su contraseña"
        },
        rememberMe: "Recordarme",
        submit: "Iniciar Sesión",
        loading: "Iniciando sesión..."
      },
      support: {
        text: "¿Tiene problemas?",
        link: "Contactar soporte",
        modal: {
          title: "Contactar Soporte",
          company: "Tartoro",
          founder: {
            name: "David C. Morales",
            title: "Fundador | Desarrollo de Negocios",
            phone: "Celular: 385-556-8823",
            email: "Correo: support@tartoro.com"
          },
          close: "Cerrar"
        }
      },
      errors: {
        loginFailed: "Error al iniciar sesión. Por favor, inténtelo de nuevo."
      }
    },
    agentLogin: {
      welcome: {
        title: "Bienvenido al Portal de Agentes",
        subtitle: "Acceda a su panel de agente y gestione cuentas de clientes eficientemente.",
        features: {
          manage: "Gestione múltiples cuentas de clientes",
          track: "Rastree el progreso y estado de los clientes",
          documents: "Acceda a documentos e información de clientes"
        }
      },
      form: {
        title: "Inicio de Sesión de Agente",
        subtitle: "Ingrese sus credenciales para acceder al panel de agente",
        email: {
          label: "Correo Electrónico",
          placeholder: "Ingrese su correo electrónico"
        },
        password: {
          label: "Contraseña",
          placeholder: "Ingrese su contraseña"
        },
        rememberMe: "Recordarme",
        submit: "Iniciar Sesión",
        loading: "Iniciando sesión..."
      },
      support: {
        text: "¿Tiene problemas?",
        link: "Contactar soporte",
        modal: {
          title: "Contactar Soporte",
          company: "Tartoro",
          founder: {
            name: "David C. Morales",
            title: "Fundador | Desarrollo de Negocios",
            phone: "Celular: 385-556-8823",
            email: "Correo: support@tartoro.com"
          },
          close: "Cerrar"
        }
      },
      errors: {
        loginFailed: "Error al iniciar sesión. Por favor, inténtelo de nuevo."
      }
    },
    forgotPassword: {
      welcome: {
        title: "¿Olvidaste tu Contraseña?",
        description: "¡No te preocupes! Ingresa tu correo electrónico abajo y te enviaremos un código para restablecer tu contraseña.",
        features: {
          secure: "Seguro – Mantenemos tu información segura.",
          easy: "Fácil – Configurar y administrar tu LLC es rápido y sencillo.",
          support: "Soporte – Estamos aquí para ayudarte cuando lo necesites."
        }
      },
      form: {
        email: {
          label: "Correo Electrónico",
          placeholder: "Ingresa tu correo electrónico"
        },
        otp: {
          label: "Código de Verificación",
          placeholder: "Ingresa el código de 6 dígitos"
        },
        requestOTP: "Enviar Código de Verificación",
        verifyOTP: "Verificar Código",
        loading: "Procesando...",
        otpSent: "Se ha enviado un código de verificación a tu correo electrónico.",
        resend: "¿No recibiste el código?",
        resendLink: "Reenviar"
      },
      errors: {
        requestFailed: "Failed to send verification code. Please try again.",
        verificationFailed: "Código de verificación inválido. Por favor, inténtalo de nuevo."
      }
    },
    changePassword: {
      welcome: {
        title: "Crear Nueva Contraseña",
        description: "Por favor, crea una contraseña segura para proteger tu cuenta.",
        features: {
          secure: "Seguro – Crea una contraseña segura para proteger tu cuenta.",
          easy: "Fácil – Elige una contraseña que recuerdes.",
          support: "Soporte – Estamos aquí para ayudarte cuando lo necesites."
        }
      },
      form: {
        newPassword: {
          label: "Nueva Contraseña",
          placeholder: "Ingresa nueva contraseña"
        },
        confirmPassword: {
          label: "Confirmar Contraseña",
          placeholder: "Confirma nueva contraseña"
        },
        submit: "Restablecer Contraseña",
        loading: "Procesando..."
      },
      errors: {
        passwordMismatch: "Las contraseñas no coinciden",
        passwordTooShort: "La contraseña debe tener al menos 8 caracteres",
        noEmailFound: "Sesión expirada. Por favor, inténtalo de nuevo.",
        resetFailed: "Failed to reset password. Please try again."
      }
    },
    raDashboard: {
      welcome: {
        title: "Bienvenido, {name}",
        subtitle: "Portal del Agente Registrado"
      },
      stats: {
        totalBusinesses: {
          title: "Total de Negocios",
          noNew: "No hay nuevos negocios este mes",
          new: "+{count} este mes"
        },
        requestedMail: {
          title: "Correo Solicitado",
          noPending: "No hay correo pendiente",
          pending: "{count} solicitudes pendientes"
        },
        completedMail: {
          title: "Correo Completado",
          noNew: "No hay nuevas finalizaciones",
          new: "+{count} este mes"
        },
        totalDocuments: {
          title: "Total de Documentos",
          noNew: "No hay nuevos documentos este mes",
          new: "+{count} este mes"
        }
      },
      activity: {
        title: "Actividad Reciente",
        viewAll: "Ver Todo",
        loading: "Cargando actividades...",
        retry: "Reintentar",
        noActivities: "No hay actividades recientes",
        allActivities: "Todas las Actividades",
        close: "Cerrar"
      },
      nav: {
        mainMenu: "MENÚ PRINCIPAL",
        dashboard: "Panel de Control",
        business: "Negocios",
        incomingMail: "Correo Entrante",
        mailRequest: "Solicitud de Correo",
        accountManagement: "GESTIÓN DE CUENTA",
        settings: "Configuración"
      },
      language: {
        english: "English",
        spanish: "Español"
      },
      user: {
        defaultName: "Usuario Agente",
        role: "Agente Registrado"
      },
      errors: {
        loadFailed: "Error al cargar datos del agente",
        dashboardLoadFailed: "Error al cargar datos del panel",
        activitiesLoadFailed: "Error al cargar actividades"
      }
    },
    raBusiness: {
      search: {
        placeholder: "Buscar negocios..."
      },
      loading: "Cargando negocios...",
      table: {
        businessName: "Nombre del Negocio",
        tradeName: "Nombre Comercial",
        state: "Estado",
        status: "Estado",
        ein: "EIN",
        createdDate: "Fecha de Creación",
        actions: "Acciones"
      },
      actions: {
        view: "Ver"
      },
      pending: "Pendiente",
      noSearchResults: "No se encontraron negocios que coincidan con tu búsqueda",
      noBusinesses: "No hay negocios asignados aún"
    },
    raIncomingMail: {
      search: {
        placeholder: "Buscar correo..."
      },
      loading: "Cargando correo entrante...",
      table: {
        business: "Negocio",
        type: "Tipo",
        priority: "Prioridad",
        status: "Estado",
        received: "Recibido",
        description: "Descripción",
        actions: "Acciones"
      },
      actions: {
        upload: "Subir Correo",
        view: "Ver",
        download: "Descargar"
      }, mailTypes: {
        legal: "Legal",
        tax: "Fiscal",
        general: "General",
        other: "Otro"
      },
      priorities: {
        low: "Baja",
        medium: "Media",
        high: "Alta",
        urgent: "Urgente"
      },
      statuses: {
        received: "Recibido",
        processed: "Procesado",
        forwarded: "Reenviado",
        shredded: "Triturado"
      },
      viewModal: {
        title: "Detalles del Correo",
        business: "Negocio",
        type: "Tipo de Correo",
        priority: "Prioridad",
        status: "Estado",
        received: "Fecha de Recepción",
        description: "Descripción",
        attachments: "Archivos Adjuntos"
      },
      upload: {
        title: "Subir Correo Entrante",
        form: {
          business: {
            label: "Negocio",
            placeholder: "Seleccionar Negocio"
          },
          mailType: {
            label: "Tipo de Correo",
            placeholder: "Seleccionar Tipo"
          },
          priority: {
            label: "Prioridad",
            placeholder: "Seleccionar Prioridad"
          },
          receivedDate: {
            label: "Fecha de Recepción"
          },
          description: {
            label: "Descripción",
            placeholder: "Ingrese notas o descripción adicional"
          },
          attachments: {
            label: "Archivos Adjuntos",
            dragDrop: {
              title: "Arrastre y suelte archivos aquí",
              or: "o",
              browse: "buscar archivos",
              hint: "Archivos JPEG, PNG, PDF y DOC hasta 5MB"
            }
          }
        },
        validation: {
          required: "Este campo es requerido",
          fileType: "{fileName}: Tipo de archivo inválido. Solo se permiten archivos JPEG, PNG, PDF y DOC.",
          fileSize: "{fileName}: El tamaño del archivo excede el límite de 5MB."
        },
        buttons: {
          cancel: "Cancelar",
          upload: "Subir",
          uploading: "Subiendo..."
        }
      },
      noSearchResults: "No se encontró correo que coincida con tu búsqueda",
      noMail: "No hay correo entrante aún",
      unnamedBusiness: "Negocio Sin Nombre",
      errors: {
        fetchFailed: "Error al cargar el correo entrante",
        downloadFailed: "Error al descargar el adjunto",
        uploadFailed: "Error al subir el correo"
      }
    },
    raBusinessDetails: {
      title: "Detalles del Negocio",
      sections: {
        businessInfo: {
          title: "Información del Negocio y Contacto",
          fields: {
            businessName: "Nombre del Negocio",
            tradeName: "Nombre Comercial",
            entityType: "Tipo de Entidad",
            businessCategory: "Categoría del Negocio",
            businessAddress: "Dirección del Negocio",
            state: "Estado",
            zipCode: "Código Postal",
            status: "Estado",
            ein: "EIN"
          }
        },
        responsibleParty: {
          title: "Parte Responsable",
          fields: {
            name: "Nombre",
            role: "Rol",
            ssn: "SSN",
            email: "Correo",
            phone: "Teléfono"
          }
        },
        businessOperations: {
          title: "Información del Negocio",
          fields: {
            experience: "Experiencia",
            employeeCount: "Número de Empleados",
            progress: "Progreso",
            goals: "Objetivos",
            activity: "Actividad",
            products: "Productos",
            startDate: "Fecha de Inicio"
          }
        },
        compliance: {
          title: "Cumplimiento y Operaciones",
          fields: {
            willHireEmployees: "Contratará Empleados",
            expectedEmployees: "Empleados Esperados",
            taxReturnsType: "Tipo de Declaraciones de Impuestos",
            yearEndDate: "Fecha de Fin de Año"
          }
        },
        packageInfo: {
          title: "Información del Paquete",
          fields: {
            selectedPackage: "Paquete Seleccionado",
            packageStatus: "Estado del Paquete",
            startDate: "Fecha de Inicio",
            endDate: "Fecha de Fin",
            price: "Precio",
            billingCycle: "Ciclo de Facturación",
            autoRenew: "Renovación Automática"
          }
        },
        otherInfo: {
          title: "Otra Información",
          fields: {
            registeredAgent: "Agente Registrado",
            agentId: "ID del Agente",
            customerId: "ID del Cliente"
          }
        }
      },
      navigation: {
        businesses: "Negocios",
        details: "Detalles"
      },
      states: {
        loading: "Cargando detalles del negocio...",
        error: "Error al cargar los detalles del negocio"
      }
    },
    customerHome: {
      nav: {
        informationManagement: "GESTIÓN DE INFORMACIÓN",
        companyManagement: "GESTIÓN DE EMPRESA",
        accountManagement: "GESTIÓN DE CUENTA",
        dashboard: "Panel de Control",
        businessDetails: "Detalles del Negocio",
        documents: "Documentos",
        mail: "Correo",
        users: "Usuarios",
        settings: "Configuración"
      },
      header: {
        selectBusiness: "Seleccionar Negocio",
        noBusinesses: "No hay negocios disponibles"
      },
      sidebar: {
        toggle: "Alternar Barra Lateral"
      },
      user: {
        logout: "Cerrar Sesión"
      }
    },
    customerBusinessDetails: {
      title: "Detalles del Negocio",
      sections: {
        businessInfo: {
          title: "Información del Negocio y Contacto",
          fields: {
            legalBusinessName: "Nombre Legal del Negocio",
            tradeName: "Nombre Comercial",
            entityType: "Tipo de Entidad",
            businessCategory: "Categoría del Negocio",
            ein: "Número de EIN",
            state: "Estado de Formación",
            startDate: "Fecha de Inicio",
            declarationDate: "Fecha de Declaración",
            businessAddress: "Dirección del Negocio",
            zipCode: "Código Postal",
            phoneNumber: "Número de Teléfono",
            emailAddress: "Correo Electrónico"
          }
        }
      },
      buttons: {
        edit: "Editar",
        save: "Guardar"
      },
      notSpecified: "No especificado"
    },
    customerDocuments: {
      categories: {
        all: "Todos los Documentos",
        formation: "Documentos de Formación",
        tax: "Documentos Fiscales",
        compliance: "Cumplimiento"
      },
      viewModes: {
        list: "Vista de Lista",
        grid: "Vista de Cuadrícula"
      },
      table: {
        headers: {
          name: "Nombre del Documento",
          description: "Descripción",
          category: "Categoría",
          date: "Fecha",
          actions: "Acciones"
        }
      },
      actions: {
        view: "Ver",
        download: "Descargar"
      },
      upload: {
        title: "Subir Nuevo Documento",
        dragDrop: "Arrastre y suelte sus archivos aquí o haga clic para buscar"
      },
      documents: {
        articles: {
          name: "Artículos de Organización",
          description: "Documento de Formación LLC"
        },
        receipt: {
          name: "Recibo de Presentación Estatal",
          description: "Recibo de Presentación"
        },
        fein: {
          name: "Detalles del FEIN",
          description: "ID Fiscal Federal"
        }
      }
    },
    customerMail: {
      search: {
        placeholder: 'Buscar correo...'
      },
      controls: {
        filter: 'Filtrar',
        selectMail: 'Seleccionar Correo',
        viewList: 'Vista de Lista',
        viewGrid: 'Vista de Cuadrícula'
      },
      loading: 'Cargando elementos de correo...',
      empty: {
        title: 'No se encontraron elementos de correo'
      },
      table: {
        headers: {
          id: 'ID',
          type: 'Tipo',
          priority: 'Prioridad',
          receivedDate: 'Fecha de Recepción',
          status: 'Estado',
          tracking: 'Seguimiento',
          actions: 'Acciones',
          description: 'Descripción'
        }
      },
      status: {
        received: 'Recibido',
        scan_requested: 'Escaneo Solicitado',
        scanned: 'Escaneado',
        forward_requested: 'Reenvío Solicitado',
        forwarded: 'Reenviado',
        shred_requested: 'Trituración Solicitada',
        shredded: 'Triturado'
      },
      actions: {
        view: 'Ver',
        forward: 'Reenviar',
        shred: 'Triturar',
        scanRequest: 'Solicitar Escaneo'
      },
      preview: {
        title: 'Vista Previa del Correo',
        attachments: 'Adjuntos',
        noAttachments: 'No hay adjuntos disponibles',
        preview: {
          loading: 'Cargando vista previa...',
          title: 'Vista Previa',
          unsupported: 'Vista previa no disponible para este tipo de archivo',
          downloadToView: 'Descargar para Ver'
        },
        actions: {
          zoomIn: 'Acercar',
          zoomOut: 'Alejar',
          resetZoom: 'Restablecer Zoom',
          download: 'Descargar'
        }
      }
    },
    customerUsersList: {
      title: "Usuarios y Permisos",
      addUser: "Agregar Usuario",
      viewModes: {
        list: "Lista",
        grid: "Cuadrícula"
      },
      teamMembers: "Miembros del Equipo",
      search: {
        placeholder: "Buscar usuarios..."
      },
      loading: "Cargando usuarios...",
      error: {
        message: "Error al cargar usuarios. Por favor, inténtelo de nuevo.",
        retry: "Reintentar"
      },
      empty: {
        title: "No se encontraron usuarios para este negocio.",
        addUser: "Agregar Usuario"
      },
      table: {
        headers: {
          name: "Nombre",
          email: "Correo",
          role: "Rol",
          status: "Estado",
          actions: "Acciones"
        }
      },
      roles: {
        employee: "Empleado",
        businessOwner: "Propietario del Negocio"
      },
      status: {
        active: "Activo",
        inactive: "Inactivo"
      },
      actions: {
        edit: "Editar"
      },
      modals: {
        addUser: {
          title: "Agregar Nuevo Usuario",
          form: {
            firstName: "Nombre",
            lastName: "Apellido",
            email: "Correo",
            phone: "Teléfono",
            role: "Rol",
            status: "Estado"
          },
          buttons: {
            cancel: "Cancelar",
            submit: "Agregar Usuario"
          }
        },
        editUser: {
          title: "Editar Usuario",
          form: {
            firstName: "Nombre",
            lastName: "Apellido",
            email: "Correo",
            phone: "Teléfono",
            role: "Rol",
            status: "Estado"
          },
          buttons: {
            cancel: "Cancelar",
            submit: "Actualizar Usuario"
          }
        }
      },
      rolesAndPermissions: {
        title: "Roles y Permisos"
      },
      notifications: {
        userAdded: "Usuario agregado exitosamente. La contraseña predeterminada es 1234",
        userUpdated: "Usuario actualizado exitosamente",
        error: "Error al actualizar usuario. Por favor, inténtelo de nuevo."
      }
    },
    customerSettings: {
      title: "Configuración",
      loading: "Cargando...",
      tabs: {
        profile: "Perfil",
        security: "Seguridad",
        notifications: "Notificaciones",
        package: "Paquete",
        danger: "Zona de Peligro"
      },
      profile: {
        title: "Configuración del Perfil",
        avatar: {
          changePhoto: "Cambiar Foto"
        },
        form: {
          fullName: "Nombre Completo",
          email: "Correo Electrónico",
          phoneNumber: "Número de Teléfono",
          saveChanges: "Guardar Cambios"
        }
      },
      security: {
        title: "Configuración de Seguridad",
        form: {
          currentPassword: "Contraseña Actual",
          newPassword: "Nueva Contraseña",
          confirmPassword: "Confirmar Nueva Contraseña",
          updatePassword: "Actualizar Contraseña",
          updating: "Actualizando..."
        },
        errors: {
          required: "Todos los campos de contraseña son requeridos",
          mismatch: "Las nuevas contraseñas no coinciden",
          tooShort: "La nueva contraseña debe tener al menos 8 caracteres"
        },
        success: "Contraseña actualizada exitosamente"
      },
      notifications: {
        title: "Configuración de Notificaciones",
        email: {
          title: "Notificaciones por Correo",
          subtitle: "Elige qué correos quieres recibir",
          updates: "Actualizaciones y anuncios del producto",
          security: "Alertas de seguridad",
          newsletter: "Boletín informativo"
        },
        push: {
          title: "Notificaciones Push",
          subtitle: "Gestiona tus notificaciones móviles",
          updates: "Nuevas actualizaciones",
          security: "Alertas de seguridad"
        }
      },
      package: {
        title: "Paquete e Información Adicional",
        sections: {
          packageDetails: {
            title: "Detalles del Paquete",
            selectedPackage: "Paquete Seleccionado",
            packageStatus: "Estado del Paquete",
            startDate: "Fecha de Inicio",
            endDate: "Fecha de Fin",
            status: {
              select: "Seleccionar Estado",
              active: "Activo",
              pending: "Pendiente",
              expired: "Expirado"
            }
          },
          businessInfo: {
            title: "Información del Negocio",
            activity: "Actividad del Negocio",
            employeeCount: "Número de Empleados",
            progress: "Progreso del Negocio",
            goals: "Objetivos del Negocio",
            progressOptions: {
              select: "Seleccionar Progreso",
              startup: "Inicio",
              growing: "En Crecimiento",
              established: "Establecido"
            }
          },
          responsibleParty: {
            title: "Información de la Parte Responsable",
            name: "Nombre de la Parte Responsable",
            role: "Rol",
            experience: "Experiencia en Negocios",
            reason: "Razón para Aplicar",
            experienceOptions: {
              select: "Seleccionar Experiencia",
              beginner: "Principiante (0-2 años)",
              intermediate: "Intermedio (2-5 años)",
              expert: "Experto (5+ años)"
            }
          }
        },
        saveButton: "Guardar Información del Paquete"
      },
      dangerZone: {
        title: "Zona de Peligro",
        deleteAccount: {
          title: "Eliminar Cuenta",
          description: "Eliminar permanentemente tu cuenta y todos los datos",
          button: "Eliminar Cuenta"
        }
      }
    },
    raMailRequest: {
      search: {
        placeholder: 'Buscar correo...'
      },
      filters: {
        dateRange: {
          label: 'Rango de Fechas',
          allTime: 'Todo el Tiempo',
          today: 'Hoy',
          week: 'Esta Semana',
          month: 'Este Mes'
        }
      },
      tabs: {
        requested: 'Solicitado',
        completed: 'Completado'
      },
      viewToggle: {
        grid: 'Vista de Cuadrícula',
        table: 'Vista de Tabla'
      },
      states: {
        loading: 'Cargando solicitudes de correo...',
        retry: 'Reintentar',
        empty: 'No se encontraron solicitudes de correo',
        clearFilters: 'Limpiar Filtros'
      },
      table: {
        id: 'ID',
        status: 'Estado',
        receivedDate: 'Fecha de Recepción',
        business: 'Negocio',
        type: 'Tipo',
        description: 'Descripción',
        action: 'Acción'
      },
      status: {
        received: 'Recibido',
        scan_requested: 'Escaneo Solicitado',
        scanned: 'Escaneado',
        forward_requested: 'Reenvío Solicitado',
        forwarded: 'Reenviado',
        shred_requested: 'Trituración Solicitada',
        shredded: 'Triturado'
      },
      actions: {
        scan: 'Escanear',
        forward: 'Reenviar',
        shred: 'Triturar',
        view: 'Ver'
      },
      mobile: {
        filters: 'Filtros',
        clear: 'Limpiar',
        apply: 'Aplicar'
      }
    },
    raSettings: {
      title: "Configuración",
      tabs: {
        profile: "Perfil",
        security: "Seguridad",
        notifications: "Notificaciones",
        preferences: "Preferencias"
      },
      profile: {
        title: "Configuración del Perfil",
        avatar: {
          changePhoto: "Cambiar Foto",
          removePhoto: "Eliminar Foto"
        },
        form: {
          firstName: {
            label: "Nombre",
            placeholder: "Ingrese el nombre"
          },
          lastName: {
            label: "Apellido",
            placeholder: "Ingrese el apellido"
          },
          email: {
            label: "Correo Electrónico",
            placeholder: "Ingrese el correo electrónico"
          },
          phone: {
            label: "Teléfono",
            placeholder: "Ingrese el número de teléfono"
          },
          role: {
            label: "Rol",
            value: "Agente Registrado"
          },
          saveChanges: "Guardar Cambios",
          saving: "Guardando..."
        }
      },
      security: {
        title: "Configuración de Seguridad",
        form: {
          currentPassword: {
            label: "Contraseña Actual",
            placeholder: "Ingrese la contraseña actual"
          },
          newPassword: {
            label: "Nueva Contraseña",
            placeholder: "Ingrese la nueva contraseña"
          },
          confirmPassword: {
            label: "Confirmar Contraseña",
            placeholder: "Confirme la nueva contraseña"
          },
          updatePassword: "Actualizar Contraseña",
          updating: "Actualizando..."
        },
        errors: {
          required: "Todos los campos de contraseña son requeridos",
          mismatch: "Las nuevas contraseñas no coinciden",
          tooShort: "La nueva contraseña debe tener al menos 8 caracteres"
        },
        success: "Contraseña actualizada exitosamente"
      },
      notifications: {
        title: "Configuración de Notificaciones",
        email: {
          title: "Notificaciones por Correo",
          subtitle: "Elija qué correos desea recibir",
          newMail: "Nuevo correo recibido",
          mailUpdates: "Actualizaciones de estado del correo",
          businessUpdates: "Actualizaciones de negocios",
          systemUpdates: "Actualizaciones del sistema"
        },
        push: {
          title: "Notificaciones Push",
          subtitle: "Gestionar notificaciones móviles",
          newMail: "Nuevo correo recibido",
          mailUpdates: "Actualizaciones de estado del correo",
          businessUpdates: "Actualizaciones de negocios"
        }
      },
      preferences: {
        title: "Preferencias",
        language: {
          label: "Idioma",
          options: {
            en: "English",
            es: "Español"
          }
        },
        theme: {
          label: "Tema",
          options: {
            light: "Claro",
            dark: "Oscuro",
            system: "Sistema"
          }
        },
        timezone: {
          label: "Zona Horaria",
          placeholder: "Seleccionar zona horaria"
        },
        dateFormat: {
          label: "Formato de Fecha",
          options: {
            mmddyyyy: "MM/DD/AAAA",
            ddmmyyyy: "DD/MM/AAAA",
            yyyymmdd: "AAAA/MM/DD"
          }
        }
      },
      states: {
        loading: "Cargando configuración...",
        saving: "Guardando cambios...",
        error: "Error al guardar los cambios",
        success: "Cambios guardados exitosamente"
      }
    }
  }
}

const i18n = createI18n({
  locale: localStorage.getItem('language') || 'en', // set default locale from localStorage
  fallbackLocale: 'en',
  messages,
  legacy: false, // Enable Composition API
  globalInjection: true, // Enable global injection
  silentTranslationWarn: true, // Suppress warnings for missing translations
  silentFallbackWarn: true, // Suppress warnings for fallback translations
  missingWarn: false, // Disable missing translation warnings
  fallbackWarn: false // Disable fallback warnings
})

export default i18n